// Model roles, context classes, capability profiles, bindings and usage (contracts 1.0 section 8;
// D-24, D-64, D-81). ModelRole, CapabilityProfile and ModelBinding are stored and shown in admin, so
// they are schemas. The port itself (ModelPort) lives in packages/ports.
//
// Helper types — pinned by E01-S5-T2, reported to the lead. Section 8 leaves these open:
//   - Message   { role: system | user | assistant | tool; content; toolCalls?; toolCallId? } — camelCase;
//               an adapter maps it to its wire format. The port never takes messages (D-64): the
//               gateway builds them from classed ContextItem values after the firewall.
//   - ToolCall  { id; name; arguments } — arguments is the JSON text exactly as the model produced it.
//   - ToolDef   { name; description; parameters: JsonSchema }.
//   - JsonSchema — a plain JSON Schema object (Record<string, unknown>); no library type.
//   - ModelChunk — a text delta, a tool-call delta (by index; id and name on the first delta of a
//               call), or exactly one final `done` chunk with a finish reason and optional usage.
//   - ModelError — the one non-schema export: an Error subclass with kind aborted | timeout |
//               unavailable | rejected | unscripted. Every adapter, and the fake, fails with it.
import { z } from "zod";
import { Count, IsoUtc, NonEmpty, strictObject } from "./primitives.js";
import { SecretRef } from "./primitives.js";
/** The jobs a model does; each has its own binding (D-64). */
export const ModelRole = z.enum(["router", "composer", "judge", "embedder", "vision"]);
/** The ten context classes of D-24. Policy decides, per class, which providers an item may reach. */
export const ContextClass = z.enum([
    "instructions",
    "approved-content",
    "firm-rule",
    "persona-line",
    "user-utterance",
    "conversation-history",
    "user-profile",
    "screen-structural",
    "screen-band",
    "screen-token",
]);
/** One classed piece of context handed to the gateway. `source` names where it came from (a chunk id, a field id). */
export const ContextItem = strictObject({
    class: ContextClass,
    text: z.string(),
    source: NonEmpty.optional(),
});
/** What a bound model can do, and the roles it is certified for. */
export const CapabilityProfile = strictObject({
    tools: z.boolean(),
    jsonSchema: z.boolean(),
    streaming: z.boolean(),
    vision: z.boolean(),
    contextTokens: z.number().int().positive(),
    certified: z.array(ModelRole),
});
/** The closed quirk list of D-64. */
export const ModelQuirk = z.enum(["no-tools", "no-json-schema", "no-system-role", "no-streaming"]);
/** An HTTP header name (RFC 9110 token). */
const HeaderName = z
    .string()
    .regex(/^[A-Za-z0-9!#$%&'*+.^_`|~-]+$/, "expected an HTTP header name");
export const ModelAuth = strictObject({
    scheme: z.enum(["bearer", "header", "none"]),
    header: HeaderName.optional(),
}).superRefine((auth, ctx) => {
    if (auth.scheme === "header" && auth.header === undefined) {
        ctx.addIssue({
            code: "custom",
            path: ["header"],
            message: "scheme header needs a header name",
        });
    }
    if (auth.scheme !== "header" && auth.header !== undefined) {
        ctx.addIssue({
            code: "custom",
            path: ["header"],
            message: "header is only allowed with scheme header",
        });
    }
});
/** A role's binding to a provider and model (D-64). Never holds a secret; secretRef is resolved by SecretsPort. */
export const ModelBinding = strictObject({
    role: ModelRole,
    provider: z.enum(["openrouter", "openai-compatible", "scripted"]),
    deployment: z.enum(["external", "private"]),
    baseUrl: z.url({ protocol: /^https?$/ }),
    model: NonEmpty,
    secretRef: SecretRef,
    auth: ModelAuth,
    timeoutMs: z.number().int().positive(),
    maxRetries: Count,
    routing: strictObject({
        zdr: z.boolean(),
        dataCollection: z.literal("deny"),
        providers: z.array(NonEmpty).optional(),
    }).optional(),
    tls: strictObject({
        caRef: NonEmpty.optional(),
        clientCertRef: NonEmpty.optional(),
    }).optional(),
    quirks: z
        .array(ModelQuirk)
        .refine((qs) => new Set(qs).size === qs.length, "a quirk is listed once")
        .optional(),
});
/** One row per model call (D-81), written by the gateway. */
export const ModelUsage = strictObject({
    at: IsoUtc,
    role: ModelRole,
    provider: NonEmpty,
    model: NonEmpty,
    inputTokens: Count,
    outputTokens: Count,
    ms: Count,
    costUsd: z.number().nonnegative().optional(),
});
/** Why a model call failed. */
export const ModelErrorKind = z.enum([
    "aborted",
    "timeout",
    "unavailable",
    "rejected",
    "unscripted",
]);
/** The one error every model adapter, and the fake, fails with. The message never carries prompt text. */
export class ModelError extends Error {
    kind;
    constructor(kind, message, options) {
        super(message ?? `model call failed: ${kind}`, options);
        this.name = "ModelError";
        this.kind = kind;
    }
}
//# sourceMappingURL=model.js.map